import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {PAGE_WATERMARK_MODE} from '@PosterWhiteboard/page/page-watermark.class';
import type * as Fabric from '@postermywall/fabricjs-2';
import {secondsToMicroSeconds} from '@Utils/math.util';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {isVideoBackground} from '@PosterWhiteboard/page/background/background.types';

interface GenerateFrameForPosterTimeResponse {
  imageData: string;
  logData: Record<string, number>;
}

/**
 * The generation-side entry points puppeteer drives. Their video-page guards must stay a
 * superset of the backend rule that routed the page here — `PageVO::needsFrameByFrameRender()`
 * — so `Page.isVideo()` (the live face of `isPageVideo`, which counts authored item timings)
 * is the only predicate they may use. A narrower local rule rejects pages the backend
 * legitimately sent to frame-by-frame render and fails the whole order.
 */
export class PosterVideoGeneration {
  private poster: Poster;

  constructor(poster: Poster) {
    this.poster = poster;
  }

  public async initGenerateFramesForVideoPoster(): Promise<void> {
    if (this.poster.mode.details.type !== PosterModeType.GENERATE) {
      throw new Error('Incorrect poster mode.It should be generate.');
    }

    const page = this.poster.getCurrentPage();
    if (!page.isVideo()) {
      throw new Error('Page has no time-varying content, so no video can be generated for the poster');
    }

    await page.pageWatermark.setWatermark({
      mode: this.poster.mode.details.showLargeWatermarkOnVideoGeneration ? PAGE_WATERMARK_MODE.VIDEO_LARGE : PAGE_WATERMARK_MODE.NONE,
    });
    page.showWhiteCanvasBackground();
  }

  public async initGenerateFramesForVideoPage(pageId: string, frameExtension?: string): Promise<void> {
    if (this.poster.mode.details.type !== PosterModeType.GENERATE) {
      throw new Error('Incorrect poster mode.It should be generate.');
    }

    const page = this.poster.pages.getPage(pageId);
    if (!page) {
      throw new Error(`Page with hashedId: ${pageId}, not found in poster.`);
    }
    if (!page.isVideo()) {
      throw new Error(`Page has no time-varying content, so no video can be generated for page with id: ${page.hashedID}`);
    }

    await page.pageWatermark.setWatermark({
      mode: this.poster.mode.details.showLargeWatermarkOnVideoGeneration ? PAGE_WATERMARK_MODE.VIDEO_LARGE : PAGE_WATERMARK_MODE.NONE,
    });
    if (frameExtension !== 'png') {
      page.showWhiteCanvasBackground();
    }
  }

  public getVideoItemFrameTimesForPosterTime(posterTime: number): Record<string, number> {
    const page = this.poster.getCurrentPage();
    const pageTime = posterTime - this.poster.pages.getStartTimeForPage(page.hashedID);
    const videoFrameTimesHashMap: Record<string, number> = {};
    const videoItems = [...page.items.getVideoItems(), ...page.items.getVideoSlides()];
    for (const videoItem of videoItems) {
      videoFrameTimesHashMap[videoItem.uid] = secondsToMicroSeconds(videoItem.getFrameTimeForPageTime(pageTime));
    }

    const activeBg = page.items.getActiveBackground();
    if (isVideoBackground(activeBg)) {
      videoFrameTimesHashMap[activeBg.uid] = secondsToMicroSeconds(activeBg.getFrameTimeForPageTime(pageTime));
    }

    return videoFrameTimesHashMap;
  }

  public getVideoItemFrameTimesForPageTime(pageId: string, pageTime: number): Record<string, number> {
    const page = this.poster.pages.getPage(pageId);
    if (!page) {
      throw new Error(`Page with hashedId: ${pageId}, not found in poster.`);
    }
    const videoFrameTimesHashMap: Record<string, number> = {};
    const videoItems = [...page.items.getVideoItems(), ...page.items.getVideoSlides()];
    for (const videoItem of videoItems) {
      videoFrameTimesHashMap[videoItem.uid] = secondsToMicroSeconds(videoItem.getFrameTimeForPageTime(pageTime));
    }

    const activeBg = page.items.getActiveBackground();
    if (isVideoBackground(activeBg)) {
      videoFrameTimesHashMap[activeBg.uid] = secondsToMicroSeconds(activeBg.getFrameTimeForPageTime(pageTime));
    }

    return videoFrameTimesHashMap;
  }

  public async generateFrameForPosterTime(posterTime: number, videoItemFrames: Record<string, string>): Promise<GenerateFrameForPosterTimeResponse> {
    const page = this.poster.getCurrentPage();
    const pageTime = posterTime - this.poster.pages.getStartTimeForPage(page.hashedID);
    const promises = [];
    const startTime = performance.now();
    promises.push(this.setVideoItemsFrame(videoItemFrames));
    promises.push(this.setGenerationSeekableItemsFrame(posterTime));
    promises.push(this.setSlideshowItemsFrame(pageTime, videoItemFrames));

    this.toggleItemVisibility(posterTime);
    await Promise.all(promises);
    this.setPageItemsAnimationFrame(page, posterTime);
    page.transitions.renderer.setFrame(pageTime);
    const setFrameTime = performance.now();

    this.poster.getCurrentPage().fabricCanvas.renderAll();
    const renderAllTime = performance.now();

    const imageData = this.poster.getCurrentPage().fabricCanvas.toDataURL({
      format: 'jpeg' as Fabric.ImageFormat,
      enableRetinaScaling: false,
      multiplier: 1,
      quality: this.poster.isHighRes ? 0.98 : 0.8,
    });
    const captureCanvasTime = performance.now();

    return {
      imageData,
      logData: {
        setFrameTime: setFrameTime - startTime,
        renderAllTime: renderAllTime - setFrameTime,
        captureCanvasTime: captureCanvasTime - renderAllTime,
        totalTimeToGeneratePosterFromFrontEnd: performance.now() - startTime,
      },
    };
  }

  public async generateFrameForPageTime(pageId: string, pageTime: number, extension: string, videoItemFrames: Record<string, string>): Promise<GenerateFrameForPosterTimeResponse> {
    const page = this.poster.pages.getPage(pageId);
    if (!page) {
      throw new Error(`Page with hashedId: ${pageId}, not found in poster.`);
    }
    const promises = [];
    const startTime = performance.now();
    promises.push(this.setVideoItemsFrame(videoItemFrames));
    promises.push(this.setGenerationSeekableItemsFrame(pageTime));
    promises.push(this.setSlideshowItemsFrame(pageTime, videoItemFrames));

    this.toggleItemVisibility(pageTime);
    await Promise.all(promises);
    this.setPageItemsAnimationFrame(page, pageTime);
    page.transitions.renderer.setFrame(pageTime);
    this.refreshTransparentBackgroundForFrame(page, pageTime, extension);
    const setFrameTime = performance.now();

    page.fabricCanvas.renderAll();
    const renderAllTime = performance.now();

    const imageData = page.fabricCanvas.toDataURL({
      format: extension === 'jpg' ? 'jpeg' : (extension as Fabric.ImageFormat),
      enableRetinaScaling: false,
      multiplier: 1,
      quality: this.poster.isHighRes ? 0.98 : 0.8,
    });
    const captureCanvasTime = performance.now();

    return {
      imageData,
      logData: {
        setFrameTime: setFrameTime - startTime,
        renderAllTime: renderAllTime - setFrameTime,
        captureCanvasTime: captureCanvasTime - renderAllTime,
        totalTimeToGeneratePosterFromFrontEnd: performance.now() - startTime,
      },
    };
  }

  /**
   * The per-frame visibility refresh recomputes canvas transparency from the page
   * clock, which generation never advances — so it keeps the time-0 background's
   * transparency for every frame, and a transparent background that follows an
   * opaque one renders opaque. Re-evaluate against the actual frame time. Only
   * for png output: jpeg/gif/video can't carry alpha and are intentionally left
   * opaque (white) as set at generation init.
   */
  private refreshTransparentBackgroundForFrame(page: Page, frameTime: number, extension: string): void {
    if (extension === 'png') {
      page.refreshFabricCanvasBackground(frameTime);
    }
  }

  private toggleItemVisibility(time: number): void {
    Object.keys(this.poster.getCurrentPage().items.itemsHashMap).forEach((key) => {
      const item = this.poster.getCurrentPage().items.itemsHashMap[key];
      item.updateItemVisibility(time);
    });
  }

  private setPageItemsAnimationFrame(page: Page, frameTime: number): void {
    Object.keys(page.items.itemsHashMap).forEach((key) => {
      const item = page.items.itemsHashMap[key];
      if (item.hasIntroAnimation()) {
        item.introAnimation.animation.setFrame(frameTime);
      }
      if (item.hasOutroAnimation()) {
        item.outroAnimation.animation.setFrame(frameTime);
      }
    });
  }

  private async setVideoItemsFrame(videoItemFrames: Record<string, string>): Promise<void> {
    const page = this.poster.getCurrentPage();
    const videoItems = page.items.getVideoItems();

    const setVideoFramePromises = [];

    if (videoItems) {
      for (const videoItem of videoItems) {
        setVideoFramePromises.push(videoItem.setImageFrame(videoItemFrames[videoItem.uid]));
      }
    }

    const activeBg = page.items.getActiveBackground();
    if (isVideoBackground(activeBg) && videoItemFrames[activeBg.uid]) {
      setVideoFramePromises.push(activeBg.setImageFrame(videoItemFrames[activeBg.uid]));
    }

    await Promise.all(setVideoFramePromises);
  }

  private async setGenerationSeekableItemsFrame(frameTime: number): Promise<void> {
    const items = this.poster.getCurrentPage().items.getGenerationSeekableItems();

    if (!items) {
      return;
    }

    const setGenerationSeekableItemsFramePromises = [];

    for (const item of items) {
      setGenerationSeekableItemsFramePromises.push(item.seek(frameTime));
    }

    await Promise.all(setGenerationSeekableItemsFramePromises);
  }

  public async setSlideshowItemsFrame(frameTime: number, videoItemFrames: Record<string, string>): Promise<void> {
    const slideshowItems = this.poster.getCurrentPage().items.getSlideshowItems();

    if (!slideshowItems) {
      return;
    }

    const promises = [];
    for (const slideshowItem of slideshowItems) {
      promises.push(slideshowItem.setFrameForGeneration(frameTime, videoItemFrames));
    }

    await Promise.all(promises);
  }
}
